import React from 'react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

/**
 * @fileoverview Componente de tabla para mostrar downtime global por paso.
 * @module AvailabilityDowntimeGlobal
 */

/**
 * Componente que procesa y muestra una tabla con información de downtime global por paso.
 * Muestra fechas, horas de inicio/término, duración y tipo de evento (downtime o no monitoreo).
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.externalData - Datos externos de la API con información del objetivo.
 * @param {Object} props.externalData.Objetivo - Objeto que contiene los datos del objetivo.
 * @param {Array} props.externalData.Objetivo.Paso - Array de pasos con información de estado.
 *
 * @returns {React.ReactElement} Tabla con información de downtime global.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
const AvailabilityDowntimeGlobal = ({ externalData }) => {
  const { t } = useTranslation();

  const processedData = React.useMemo(() => {
    const data = externalData?.Objetivo;
    let arrayRows = [];
    let noMonCum = 0;
    let downCum = 0;

    // Obtener el estado del primer paso (estructura de DowntimeGlobal)
    const estado = data?.Paso?.[0]?.estado || [];

    estado.forEach((state) => {
      if (state.nombre === 'no monitoreo' || state.nombre === 'downtime') {
        const fechaDesde = new Date(state.fecha_desde);
        const fechaHasta = new Date(state.fecha_hasta);
        const duration = (fechaHasta.getTime() - fechaDesde.getTime()) / 1000;
        const durationStr = new Date(duration * 1000)
          .toISOString()
          .substring(11, 19);

        if (durationStr !== '00:00:00') {
          const row = {
            tiempo_duracion: duration,
            duracion: durationStr,
            fecha: state.fecha_desde.substring(0, 10),
            hora_inicio: state.fecha_desde.substring(11, 19),
            hora_termino: state.fecha_hasta.substring(11, 19),
            fecha_inicio: moment(state.fecha_desde),
            fecha_termino: moment(state.fecha_hasta),
            fechaStrInicio: state.fecha_desde,
            fechaStrTermino: state.fecha_hasta,
            nombre: state.nombre,
          };

          arrayRows.push(row);

          if (state.nombre === 'no monitoreo') {
            noMonCum += duration;
          } else if (state.nombre === 'downtime') {
            downCum += duration;
          }
        }
      }
    });

    // Ordenar por fecha de inicio
    arrayRows.sort((a, b) => a.fecha_inicio - b.fecha_inicio);

    // Formatear acumulados
    let noMonCumFormatted = '00:00:00';
    let downCumFormatted = '00:00:00';

    if (arrayRows.length > 0) {
      // Formatear no monitoreo acumulado
      const daysNoMon = Math.floor(noMonCum / 86400);
      if (daysNoMon > 0) {
        noMonCumFormatted = `${daysNoMon} día(s) ${new Date(noMonCum * 1000)
          .toISOString()
          .substring(11, 19)}`;
      } else {
        noMonCumFormatted = new Date(noMonCum * 1000)
          .toISOString()
          .substring(11, 19);
      }

      // Formatear downtime acumulado
      const daysDown = Math.floor(downCum / 86400);
      if (daysDown > 0) {
        downCumFormatted = `${daysDown} día(s) ${new Date(downCum * 1000)
          .toISOString()
          .substring(11, 19)}`;
      } else {
        downCumFormatted = new Date(downCum * 1000)
          .toISOString()
          .substring(11, 19);
      }
    }

    return {
      rows: arrayRows,
      noMonCum: noMonCumFormatted,
      downCum: downCumFormatted,
    };
  }, [externalData]);

  const { rows, noMonCum, downCum } = processedData;

  return (
    <div>
      <TableData
        rows={rows}
        name={t('availability.name-graph.6')}
        noMonCum={noMonCum}
        downCum={downCum}
        t={t}
      />
    </div>
  );
};

/**
 * Componente interno que renderiza la tabla de datos.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Array} props.rows - Filas de datos a mostrar.
 * @param {string} props.name - Nombre/título de la tabla.
 * @param {string} props.noMonCum - Tiempo acumulado de no monitoreo.
 * @param {string} props.downCum - Tiempo acumulado de downtime.
 * @param {Function} props.t - Función de traducción.
 *
 * @returns {React.ReactElement} Tabla renderizada.
 */
function TableData({ rows, name, noMonCum, downCum, t }) {
  return (
    <Table id="table_downtime_global_0" aria-label="spanning table">
      <TableHead>
        <TableRow>
          <TableCell colSpan={5}>{name}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            {t('availability-downtime-global.table.header.start-date')}
          </TableCell>
          <TableCell>
            {t('availability-downtime-global.table.header.start-time')}
          </TableCell>
          <TableCell>
            {t('availability-downtime-global.table.header.end-time')}
          </TableCell>
          <TableCell>
            {t('availability-downtime-global.table.header.duration')}
          </TableCell>
          <TableCell>
            {t('availability-downtime-global.table.header.type')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length > 0 ? (
          rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.fecha}</TableCell>
              <TableCell>{row.hora_inicio}</TableCell>
              <TableCell>{row.hora_termino}</TableCell>
              <TableCell>{row.duracion}</TableCell>
              <TableCell>
                {t(`availability-downtime-global.states.${row.nombre}`)}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={5} align="center">
              {t('availability-downtime-global.table.footer.no-global')}
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell rowSpan={3} />
          <TableCell colSpan={3}>
            {t('availability-downtime-global.table.footer.total-downtime')}
          </TableCell>
          <TableCell>{downCum}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3}>
            {t('availability-downtime-global.table.footer.total-no-monitoring')}
          </TableCell>
          <TableCell>{noMonCum}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export default AvailabilityDowntimeGlobal;
